import { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Autocomplete,
  Option,
} from '@beeline/design-system-react/extracted-styles';

import { useFloorMap } from 'src/components/FloorMap/FloorMapProvider';
import { getFilteredDesks } from 'src/store/desks/selectors';
import { openDesk } from 'src/store/desks/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { PlaceType } from 'src/types';

const renderValue = (option: Option<string>): string => option.value ?? '';

export interface DeskSearchProps {
  onOpen?: () => void;
}

export const DeskSearch: FC<DeskSearchProps> = ({ onOpen }) => {
  const dispatch = useAppDispatch();

  const filteredBookingObjects = useSelector(getFilteredDesks);
  const { scrollTo } = useFloorMap();

  const options = useMemo((): Option<string>[] => {
    const result: Option<string>[] = [];

    for (const [key, value] of filteredBookingObjects) {
      if (!key || !value.title) continue;

      result.push({ id: key, value: value.title });
    }

    return result.sort((a, b) => a.value.localeCompare(b.value));
  }, [filteredBookingObjects]);

  const [value, setValue] = useState<Option<string> | null>(null);
  const [input, setInput] = useState('');

  const filteredOptions = useMemo((): Option<string>[] => {
    const search = input.toLowerCase();

    return options.filter(option =>
      option.value.toLowerCase().includes(search),
    );
  }, [input, options]);

  const handleChange = useCallback(
    async (option: Option<string>) => {
      setValue(option);

      const found = filteredBookingObjects.get(option.id as string);
      if (found && found.type === PlaceType.Desk) {
        onOpen?.();

        dispatch(openDesk(found.mapPlaceId));

        scrollTo(found);
      }
    },
    [dispatch, filteredBookingObjects, onOpen, scrollTo],
  );

  const handleClear = useCallback(() => {
    setValue(null);
  }, []);

  return (
    <Autocomplete
      placeholder='Место'
      type='search'
      size='medium'
      disabled={!options.length}
      options={filteredOptions}
      value={value}
      noOptionsText='рабочее место не найдено'
      onChange={handleChange}
      renderValue={renderValue}
      onInputChange={setInput}
      onInputClear={handleClear}
      loading={!options.length}
      fullWidth
    />
  );
};
